import React, { useState, useEffect, useCallback } from 'react';
// Material-UI
import Button from '@material-ui/core/Button';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import Snackbar from '@material-ui/core/Snackbar';
import CircularProgress from '@material-ui/core/CircularProgress';
import { CircleCheck, AlertCircle } from 'tabler-icons-react';
// Styles
import '../../styles/noproduccion.css';
import '../../styles/notifications.css';
import '../../App.css';

const BASE = process.env.REACT_APP_SERVICE_ENDPOINT;

// ── Helpers ───────────────────────────────────────────────────────────────────

function toReadableTime(secs) {
    const n = Number(secs);
    if (!secs || isNaN(n) || n <= 0) return null;
    const m = Math.floor(n / 60);
    const s = n % 60;
    if (m === 0) return `${s} seg`;
    return s === 0 ? `${m} min` : `${m} min ${s} seg`;
}

// ── Main view ─────────────────────────────────────────────────────────────────

export default function NoProduccion() {
    const tokenSessionObject = JSON.parse(sessionStorage.getItem('token'));
    const token = tokenSessionObject?.token;

    const [lines, setLines] = useState([]);
    const [selectedLine, setSelectedLine] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // Config fields
    const [timeout, setTimeout_] = useState('');
    const [mode, setMode] = useState('factor');
    const [factor, setFactor] = useState('');
    const [customSeconds, setCustomSeconds] = useState('');

    // Errors
    const [factorError, setFactorError] = useState('');
    const [customError, setCustomError] = useState('');
    const [timeoutError, setTimeoutError] = useState('');

    // Snackbar
    const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

    // ── Fetch lines ──
    const getLines = useCallback(async () => {
        try {
            const res = await fetch(`${BASE}/lineas`, {
                headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
            });
            if (res.ok) setLines(await res.json());
        } catch { /* ignore */ }
    }, [token]);

    useEffect(() => { getLines(); }, [getLines]);

    // ── Fetch config for selected line ──
    const getConfig = useCallback(async (lineId) => {
        setLoading(true);
        try {
            const res = await fetch(`${BASE}/AlertasNoProd/${lineId}`, {
                headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
            });
            if (res.ok) {
                const data = await res.json();
                setTimeout_(String(data.Timeout ?? data.timeout ?? ''));
                const modoDeteccion = data.ModoDeteccion ?? data.modoDeteccion;
                const valor = data.valor ?? data.Valor ?? '';
                if (modoDeteccion === 2) {
                    setMode('personalizado');
                    setCustomSeconds(String(valor));
                    setFactor('');
                } else {
                    setMode('factor');
                    setFactor(String(valor));
                    setCustomSeconds('');
                }
            } else {
                // No config yet — reset to defaults
                setTimeout_('');
                setMode('factor');
                setFactor('');
                setCustomSeconds('');
            }
        } catch {
            setTimeout_('');
            setMode('factor');
            setFactor('');
            setCustomSeconds('');
        }
        setFactorError('');
        setCustomError('');
        setTimeoutError('');
        setLoading(false);
    }, [token]);

    const handleSelectLine = (line) => {
        setSelectedLine(line);
        getConfig(line.id);
    };

    // ── Handlers ──
    const handleModeChange = (e) => {
        setMode(e.target.value);
        setFactorError('');
        setCustomError('');
    };

    const handleFactorChange = (e) => {
        setFactor(e.target.value);
        setMode('factor');
        if (factorError && e.target.value) setFactorError('');
    };

    const handleCustomChange = (e) => {
        setCustomSeconds(e.target.value);
        setMode('personalizado');
        if (customError && e.target.value) setCustomError('');
    };

    const handleTimeoutChange = (e) => {
        setTimeout_(e.target.value);
        if (timeoutError && e.target.value) setTimeoutError('');
    };

    // ── Validate ──
    const validate = () => {
        let valid = true;
        // Timeout
        const t = Number(timeout);
        if (timeout === '' || isNaN(t) || !Number.isInteger(t) || t < 0) {
            setTimeoutError('Ingresa 0 para deshabilitar o un número entero positivo');
            valid = false;
        }
        // Detección
        if (mode === 'factor') {
            const n = Number(factor);
            if (!factor || isNaN(n) || n <= 1) {
                setFactorError('Ingresa un valor mayor a 1 (ej. 1.5)');
                valid = false;
            }
        } else {
            const n = Number(customSeconds);
            if (!customSeconds || !Number.isInteger(n) || n < 1) {
                setCustomError('Ingresa un número entero mayor a 0');
                valid = false;
            }
        }
        return valid;
    };

    // ── Save ──
    const handleSave = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            const body = {
                linea: selectedLine.id,
                Timeout: Number(timeout),
                ModoDeteccion: mode === 'factor' ? 1 : 2,
                valor: mode === 'factor' ? Number(factor) : Number(customSeconds),
            };
            const res = await fetch(`${BASE}/AlertasNoProd`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body),
            });
            if (res.ok) {
                setSnack({ open: true, msg: 'Configuración guardada', severity: 'success' });
            } else {
                setSnack({ open: true, msg: 'Error al guardar configuración', severity: 'error' });
            }
        } catch {
            setSnack({ open: true, msg: 'Error de conexión', severity: 'error' });
        }
        setSaving(false);
    };

    const timeoutHint = toReadableTime(timeout);

    return (
        <div className="noprod-page">
            <div className="noprod-header">
                <h1 className="titulo">Alertas — No Producción</h1>
                <p className="desc">Configura los parámetros de detección de paros por línea de producción.</p>
            </div>

            <div className="noprod-body">
                {/* Panel líneas */}
                <div className="noprod-panel-lines">
                    <div className="noprod-card">
                        <div className="noprod-card-toolbar">
                            <h2 className="noprod-card-title">Líneas</h2>
                        </div>
                        <div className="noprod-lines-list">
                            {lines.map((line) => (
                                <div
                                    key={line.id}
                                    className={`noprod-line-item${selectedLine?.id === line.id ? ' noprod-line-item--selected' : ''}`}
                                    onClick={() => handleSelectLine(line)}
                                >
                                    {line.nombre}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Panel configuración */}
                <div className="noprod-panel-main">
                    {selectedLine ? (
                        loading ? (
                            <div className="noprod-card noprod-card--empty">
                                <CircularProgress size={32} />
                            </div>
                        ) : (
                            <div className="noprod-config-area">
                                {/* Timeout */}
                                <div className="noprod-section-card">
                                    <div className="noprod-section-header">
                                        <h3 className="noprod-section-title">Alerta de No Producción (Timeout)</h3>
                                        <p className="noprod-section-desc">
                                            Tiempo máximo sin producción antes de disparar una alerta. Ingresa{' '}
                                            <strong>0</strong> para deshabilitar la alerta en esta línea.
                                        </p>
                                    </div>
                                    <div className="noprod-section-body">
                                        <div className="noprod-input-row">
                                            <TextField
                                                type="number"
                                                value={timeout}
                                                onChange={handleTimeoutChange}
                                                placeholder="300"
                                                error={!!timeoutError}
                                                helperText={timeoutError}
                                                variant="outlined"
                                                size="small"
                                                className="noprod-seconds-field"
                                                inputProps={{ min: 0, step: 1 }}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <span className="noprod-seconds-suffix">seg</span>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                            {timeoutHint && <span className="noprod-time-hint">≈ {timeoutHint}</span>}
                                        </div>
                                    </div>
                                </div>

                                {/* Detección de paros */}
                                <div className="noprod-section-card">
                                    <div className="noprod-section-header">
                                        <h3 className="noprod-section-title">Detección de paros</h3>
                                        <p className="noprod-section-desc noprod-section-desc--nowrap">
                                            Define cuándo se registra un paro de producción. El factor de ciclo compara el tiempo transcurrido contra el tiempo de ciclo del producto activo.
                                        </p>
                                    </div>
                                    <div className="noprod-section-body">
                                        <RadioGroup value={mode} onChange={handleModeChange}>
                                            {/* Factor de ciclo */}
                                            <div className={`noprod-radio-row${mode === 'factor' ? ' noprod-radio-row--active' : ''}`}>
                                                <FormControlLabel
                                                    value="factor"
                                                    control={<Radio color="primary" size="small" />}
                                                    label={<span className="noprod-radio-label">Factor del tiempo de ciclo estándar</span>}
                                                    className="noprod-radio-control"
                                                />
                                                <div className={`noprod-custom-input${mode !== 'factor' ? ' noprod-custom-input--disabled' : ''}`}>
                                                    <TextField
                                                        type="number"
                                                        value={factor}
                                                        onChange={handleFactorChange}
                                                        onFocus={() => setMode('factor')}
                                                        placeholder="1.5"
                                                        error={!!factorError}
                                                        helperText={factorError || 'Debe ser mayor a 1 — ej. 1.5 = 1.5× el ciclo del producto a producir'}
                                                        disabled={mode !== 'factor'}
                                                        variant="outlined"
                                                        size="small"
                                                        className="noprod-factor-field"
                                                        FormHelperTextProps={{ className: 'noprod-factor-helper' }}
                                                        inputProps={{ min: 1.01, step: 0.01 }}
                                                    />
                                                </div>
                                            </div>

                                            {/* Tiempo fijo */}
                                            <div className={`noprod-radio-row${mode === 'personalizado' ? ' noprod-radio-row--active' : ''}`}>
                                                <FormControlLabel
                                                    value="personalizado"
                                                    control={<Radio color="primary" size="small" />}
                                                    label={<span className="noprod-radio-label">Tiempo fijo en segundos</span>}
                                                    className="noprod-radio-control"
                                                />
                                                <div className={`noprod-custom-input${mode !== 'personalizado' ? ' noprod-custom-input--disabled' : ''}`}>
                                                    <div className="noprod-input-row">
                                                        <TextField
                                                            type="number"
                                                            value={customSeconds}
                                                            onChange={handleCustomChange}
                                                            onFocus={() => setMode('personalizado')}
                                                            placeholder="0"
                                                            error={!!customError}
                                                            helperText={customError}
                                                            disabled={mode !== 'personalizado'}
                                                            variant="outlined"
                                                            size="small"
                                                            className="noprod-seconds-field"
                                                            inputProps={{ min: 1, step: 1 }}
                                                            InputProps={{
                                                                endAdornment: (
                                                                    <InputAdornment position="end">
                                                                        <span className="noprod-seconds-suffix">seg</span>
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                        />
                                                        {toReadableTime(customSeconds) && (
                                                            <span className="noprod-time-hint">≈ {toReadableTime(customSeconds)}</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </RadioGroup>
                                    </div>
                                </div>

                                {/* Single save button */}
                                <div className="noprod-save-bar">
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        className="noprod-btn"
                                        onClick={handleSave}
                                        disabled={saving}
                                    >
                                        {saving ? 'Guardando…' : 'Guardar'}
                                    </Button>
                                </div>
                            </div>
                        )
                    ) : (
                        <div className="noprod-card noprod-card--empty">
                            <p className="noprod-empty">Selecciona una línea para ver su configuración</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Snackbar */}
            <Snackbar
                open={snack.open}
                autoHideDuration={4000}
                onClose={() => setSnack((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <div className={`app-notif app-notif--${snack.severity}`}>
                    <div className="app-notif__msg">
                        {snack.severity === 'success' ? <CircleCheck size={18} /> : <AlertCircle size={18} />}
                        {snack.msg}
                    </div>
                    <div className="app-notif__progress" />
                </div>
            </Snackbar>
        </div>
    );
}
